import { useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { actionCreators, type State, store } from "@App/state";
import { bindActionCreators } from "@reduxjs/toolkit";
import { useGarageParameters } from "@App/hooks/url/useGarageParameters";
import { useVehicletreeParameters } from "@App/hooks/url/useVehicletreeParameters";
import { useServiceParameters } from "@App/hooks/url/useServiceParameters";
import { usePlaceParameters } from "@App/hooks/url/usePlaceParameters";
import { useGarageChainParameters } from "@App/hooks/url/useGarageChainParameters";
import { useOrganizationParameters } from "@App/hooks/url/useOrganizationParameters";
import { usePremiumServiceParameters } from "@App/hooks/url/usePremiumServiceParameters";
import { useEngineTypeParameters } from "@App/hooks/url/useEngineTypeParameters";
import { useCustomerParameters } from "@App/hooks/url/useCustomerParameters";
import { useJwtTokenParameters } from "@App/hooks/url/useJwtTokenParameters";
import { useHSNTSNParameters } from "@App/hooks/url/useHSNTSNParameters";
import { useVINParameters } from "@App/hooks/url/useVINParameters";
import { GeographyService } from "@App/apis/graphql/geography/v1/geographyService";
import { useRegistrationDateParameter } from "@App/hooks/url/useRegistrationDateParameter";
import { useRegParameter } from "@App/hooks/url/useRegParameter";
import { useAnalyticsParameters } from "@App/hooks/url/useAnalyticsParameters";
import { useShowAsNonBookableParams } from "@App/hooks/url/useShowAsNonBookableParams";
import { useFleetVehicleReturnParameters } from "@App/hooks/url/useFleetVehicleReturnParameters";
import { useMessageParameters } from "@App/hooks/url/useMessageParameters";
import { useGarageTypeIdParameters } from "@App/hooks/url/useGarageTypeIdParameters";
import { useCallbackRefParameter } from "@App/hooks/url/useCallbackRefParameter";
import { useTypeUrlParams } from "@App/hooks/url/useTypeUrlParams";

/**
 * A custom hook that checks if all needed data was loaded from the api on component mount
 * and returns a method to parse the data to the url query parameters.
 */
export const useQueryParams = () => {
  const geographyApi = new GeographyService();

  const { garageQueryParams } = useGarageParameters();
  const { vehicletreeQueryParams } = useVehicletreeParameters();
  const { serviceQueryParams } = useServiceParameters();
  const { placeQueryParams } = usePlaceParameters(geographyApi);
  const { garageChainQueryParams } = useGarageChainParameters();
  const { organizationQueryParams } = useOrganizationParameters();
  const { premiumServiceQueryParams } = usePremiumServiceParameters();
  const { engineTypeQueryParams } = useEngineTypeParameters();
  const { customerQueryParams } = useCustomerParameters();
  const { jwtTokenParams } = useJwtTokenParameters();
  const { hsntsnQueryParams } = useHSNTSNParameters();
  const { vinQueryParams } = useVINParameters();
  const { registrationDateParameter } = useRegistrationDateParameter();
  const { regParameter } = useRegParameter();
  const { analyticsQueryParams } = useAnalyticsParameters();
  const { showAsNonBookableParams } = useShowAsNonBookableParams();
  const { fleetVehicleReturnQueryParams } = useFleetVehicleReturnParameters();
  const { messageQueryParams } = useMessageParameters();
  const { garageTypeIdQueryParams } = useGarageTypeIdParameters();

  // Ingestion only — persists the partner callback token; no createQuery re-emit.
  useCallbackRefParameter();
  useTypeUrlParams();

  const dispatch = useDispatch();
  const { changeNameFilter, addSubRepair } = bindActionCreators(actionCreators, dispatch);

  const [searchParams] = useSearchParams();

  const showAsNonBookable = useSelector((state: State) => state.filters.showAsNonBookable);

  const search = useSelector((state: State) => state.filters.search);

  useEffect(() => {
    const searchQuery = searchParams.get("search");
    const subRepairsQuery = searchParams.get("subRepairs");

    // set search query if not in state
    if (searchQuery && searchQuery !== "null" && (!search || searchQuery !== search)) {
      changeNameFilter(searchQuery);
    }

    // set search query if not in state
    if (subRepairsQuery && subRepairsQuery !== "null" && (!search || subRepairsQuery !== search)) {
      subRepairsQuery.split(",").forEach((subRepair) => addSubRepair(subRepair));
    }
  }, []);

  // if search changes, automatically update the query parameters
  useEffect(() => {
    if (search && search !== searchParams.get("search")) {
      searchParams.set("search", search);
    }
    // navigate(location.pathname + createQuery(), {replace: true})
  }, [search]);

  useEffect(() => {
    if (showAsNonBookable) {
      searchParams.set("showAsNonBookable", showAsNonBookable.toString());
    } else {
      searchParams.delete("showAsNonBookable");
    }
  }, [showAsNonBookable]);

  const createQuery = (query_params?: Record<string, string>) => {
    return `?${new URLSearchParams({
      ...organizationQueryParams(),
      ...serviceQueryParams(),
      ...vehicletreeQueryParams(),
      ...garageQueryParams(),
      ...placeQueryParams(),
      ...garageChainQueryParams(),
      ...premiumServiceQueryParams(),
      ...engineTypeQueryParams(),
      ...customerQueryParams(),
      // add search if existing
      ...(store.getState().filters.search && {
        search: store.getState().filters.search,
      }),
      ...(store.getState().filters.showAsNonBookable && {
        showAsNonBookable: store.getState().filters.showAsNonBookable.toString(),
      }),
      // add subRepairs if existing
      ...(store.getState().orders.subRepairs &&
        store.getState().orders.subRepairs.length > 0 && {
          subRepairs: store.getState().orders.subRepairs.join(","),
        }),
      ...jwtTokenParams(),
      ...hsntsnQueryParams(),
      ...vinQueryParams(),
      ...registrationDateParameter,
      ...regParameter,
      ...analyticsQueryParams(),
      ...showAsNonBookableParams(),
      ...fleetVehicleReturnQueryParams(),
      ...messageQueryParams(),
      ...garageTypeIdQueryParams(),
      ...query_params,
    })}`;
  };

  return { createQuery };
};
